import { Center, HStack, Image, Link, Pressable, Stack, Text, VStack } from 'native-base'

import PrivacyOptionsSVG from '../assets/PrivacyOptions.svg'
import i18n from '../i18n'
import { trackEvent } from '../utils/analytics-v2'
import { BUILD_LABEL } from '../utils/appVersion'
import { colors } from '../utils/theme/configureTheme'

interface LegalLinkProps {
  text: string
  href?: string
  onPress?: () => void
  image?: {
    uri: string
    alt: string
    height: string
    width: string
  }
}

/** Renders a legal text link in the footer. */
function LegalLink({ link }: { link: LegalLinkProps }) {
  return (
    <Link
      key={link.text}
      href={link.href}
      isExternal
      onPress={link.onPress}
      _text={{
        fontFamily: 'Matter Medium',
        fontSize: 16,
        color: colors.brand.softBlack,
        fontWeight: '500',
        letterSpacing: -0.36,
      }}
      _hover={{
        _text: {
          color: colors.brand.vermillion,
        },
      }}
      isUnderlined={false}
    >
      {link.text}
    </Link>
  )
}

/** Renders a legal link accompanied by an inline image in the footer. */
function LegalLinkWithImage({ link }: { link: LegalLinkProps }) {
  return (
    <Pressable
      onPress={() => {
        if (link.href) {
          window.open(link.href, '_blank')
        }
        link.onPress?.()
      }}
    >
      {({ isHovered }) => (
        <HStack space={2} alignItems="center">
          <Text
            fontFamily="Matter Medium"
            fontSize={16}
            color={isHovered ? colors.brand.vermillion : colors.brand.softBlack}
            fontWeight="500"
            letterSpacing={-0.36}
          >
            {link.text}
          </Text>
          <Image
            source={{ uri: link.image?.uri }}
            alt={link.image?.alt}
            height={link.image?.height}
            width={link.image?.width}
            resizeMode={'contain'}
          />
        </HStack>
      )}
    </Pressable>
  )
}

/** Site footer containing legal links, copyright notice, build identity, and the TrustArc privacy manager. */
export default function Footer() {
  const currentYear = new Date().getFullYear()

  const legalLinks = [
    {
      text: 'Vulnerability Report',
      href: 'https://shipveho.com/vulnerability-report',
      onPress: () => trackEvent('navigate_to_vulnerability_report', { element: 'footer_link' }),
    },
    {
      text: i18n.t('common:PRIVACY_POLICY'),
      href: 'https://shipveho.com/privacy',
      onPress: () => trackEvent('navigate_to_privacy_page', { privacy_type: 'footer_link' }),
    },
    {
      text: 'Security Overview',
      href: 'https://shipveho.com/security',
      onPress: () => trackEvent('navigate_to_security_overview', { element: 'footer_link' }),
    },
    {
      text: 'Your Privacy Choices',
      onPress: () => {
        // Script is injected by DataGrail, which handles the consent banner
        window.DG_BANNER_API.showConsentBanner()
        trackEvent('open_privacy_choices', { element: 'footer_link' })
      },
      image: {
        uri: PrivacyOptionsSVG,
        alt: 'Privacy Choices Icon',
        height: '16px',
        width: '35px',
      },
    },
  ]

  return (
    <Center
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        paddingTop: 25,
        paddingBottom: 32,
      }}
    >
      <Stack
        direction={{ base: 'column-reverse', md: 'row' }}
        space={4}
        justifyContent={{ base: 'center', md: 'space-between' }}
        alignItems="center"
        width="100%"
        maxWidth={1200}
        px={6}
      >
        <VStack space={1} alignItems={{ base: 'center', md: 'flex-start' }}>
          <Text
            style={{
              fontFamily: 'Matter Regular',
              fontSize: 16,
              color: colors.brand.softBlack,
              fontWeight: '400',
              opacity: 0.6,
              letterSpacing: -0.36,
            }}
          >
            Copyright © {currentYear} Veho Tech, Inc. All rights reserved.
          </Text>
          {/*
            Deliberately quiet: smaller and fainter than the copyright it sits
            under, so it reads as a plate rather than as content. It still has to
            be legible when someone is asked to read it out, which is why the
            opacity stops at 0.5 rather than going lower.

            `userSelect` is stated rather than left to the default because this
            line exists to be copied into a ticket, and react-native-web's Text
            is one `user-select: none` on an ancestor away from silently losing
            that. Spelled as a style rather than the `selectable` prop, which
            RNW deprecates in favour of exactly this. `testID` gives the deploy
            smoke suite a stable hook if it ever wants to assert the live commit.
          */}
          <Text
            testID="app-build-label"
            style={{
              fontFamily: 'Matter Regular',
              fontSize: 13,
              color: colors.brand.softBlack,
              fontWeight: '400',
              opacity: 0.5,
              letterSpacing: -0.2,
              userSelect: 'text',
            }}
          >
            {BUILD_LABEL}
          </Text>
        </VStack>
        <HStack space={4} flexWrap="wrap" justifyContent={{ base: 'center', md: 'flex-end' }}>
          {legalLinks.map(link => {
            if (link.image) {
              return <LegalLinkWithImage key={`${link.text}-with-image`} link={link} />
            }

            return <LegalLink key={`${link.text}-link`} link={link} />
          })}
        </HStack>
      </Stack>
    </Center>
  )
}
